import { writeFetch } from '../api';

const PLANS_TABLE = 'subscription_plans_1776483795115';
const TIMEOUT_MS  = 12_000;

export const DEFAULT_PLANS = [
  {
    id: 'seed-free', plan_key: 'free', name: 'Free',
    description: 'Get started at no cost', storage_mb: 100, price_usd: 0,
    billing_cycle: 'forever',
    features: ['100 MB secure storage', 'Up to 5 documents', 'AES-256 encryption', 'Basic access control'],
    highlight: false, is_active: true, sort_order: 0,
    color: 'from-gray-400 to-gray-500', badge: 'bg-gray-100 text-gray-600 border-gray-200',
  },
  {
    id: 'seed-starter', plan_key: 'starter', name: 'Starter',
    description: 'For individuals', storage_mb: 1024, price_usd: 4.99,
    billing_cycle: 'month',
    features: ['1 GB secure storage', 'Unlimited documents', 'AES-256 encryption', 'Multi-party authorization', 'Priority support'],
    highlight: false, is_active: true, sort_order: 1,
    color: 'from-blue-500 to-blue-700', badge: 'bg-blue-50 text-blue-700 border-blue-200',
  },
  {
    id: 'seed-family', plan_key: 'family', name: 'Family',
    description: 'Ideal for families', storage_mb: 5120, price_usd: 12.99,
    billing_cycle: 'month',
    features: ['5 GB secure storage', 'Unlimited documents', 'AES-256 encryption', 'Multi-party authorization', 'Document expiry alerts', 'Priority support'],
    highlight: true, is_active: true, sort_order: 2,
    color: 'from-amber-400 to-amber-600', badge: 'bg-amber-50 text-amber-700 border-amber-200',
  },
  {
    id: 'seed-legacy', plan_key: 'legacy', name: 'Legacy',
    description: 'Full-service estate', storage_mb: 20480, price_usd: 29.99,
    billing_cycle: 'month',
    features: ['20 GB secure storage', 'Unlimited documents', 'AES-256 encryption', 'Multi-party authorization', 'Document expiry alerts', 'Dedicated account manager', 'White-glove onboarding'],
    highlight: false, is_active: true, sort_order: 3,
    color: 'from-purple-600 to-indigo-900', badge: 'bg-purple-50 text-purple-700 border-purple-200',
  },
  {
    id: 'seed-will-draft', plan_key: 'will_draft', name: 'Will Draft',
    description: 'One-time generation', storage_mb: 0, price_usd: 29,
    billing_cycle: 'one-time',
    features: ['Full AI-guided 4-step interview', 'Jurisdiction-aware legal language (30+ countries)', 'Downloadable PDF will draft', 'Executor & beneficiary clauses', 'Legal disclaimer & next-steps guide'],
    highlight: false, is_active: true, sort_order: 10,
    color: 'from-blue-50 to-blue-100', badge: '',
  },
  {
    id: 'seed-will-guardian', plan_key: 'will_guardian', name: 'Legacy Guardian',
    description: 'Ongoing will management', storage_mb: 50, price_usd: 15,
    billing_cycle: 'month',
    features: ['Everything in Will Draft', 'Unlimited will updates & revisions', 'Annual legal check-in reminders', 'Life-event alerts', 'Digital asset vault (up to 50 entries)', '1x attorney consultation per year', 'Beneficiary notification service', 'Priority email & chat support'],
    highlight: true, is_active: true, sort_order: 11,
    color: 'from-accent-navy to-[#1a2a5e]', badge: 'Most Popular',
  },
  {
    id: 'seed-will-estate', plan_key: 'will_estate', name: 'Estate Concierge',
    description: 'Complete estate management', storage_mb: 1024, price_usd: 39,
    billing_cycle: 'month',
    features: ['Everything in Legacy Guardian', 'Unlimited digital asset vault entries', 'Multi-jurisdiction document management', 'Dedicated estate concierge (human)', 'Quarterly attorney reviews', 'Family member sub-accounts (up to 4)', 'Trust & power of attorney drafting', 'Probate assistance & filing guidance', 'White-glove document notarisation service'],
    highlight: false, is_active: true, sort_order: 12,
    color: 'from-purple-50 to-purple-100', badge: 'Full Protection',
  }
];

export function formatStorage(mb) {
  if (mb >= 1024) return `${(mb / 1024).toFixed(0)} GB`;
  return `${mb} MB`;
}

export function parsePlan(row) {
  if (!row) return null;
  let features = row.features;

  if (Array.isArray(features)) {
    features = features.map(String).filter(Boolean);
  } else if (typeof features === 'string' && features.trim()) {
    try {
      const parsed = JSON.parse(features);
      if (Array.isArray(parsed)) {
        features = parsed.map(String).filter(Boolean);
      } else if (typeof parsed === 'string') {
        features = [parsed].filter(Boolean);
      } else {
        features = features.split('\n').map(f => f.trim()).filter(Boolean);
      }
    } catch {
      if (features.includes('\n')) {
        features = features.split('\n').map(f => f.trim()).filter(Boolean);
      } else if (features.includes(',')) {
        features = features.split(',').map(f => f.trim()).filter(Boolean);
      } else {
        features = [features.trim()].filter(Boolean);
      }
    }
  } else {
    features = [];
  }

  return {
    ...row,
    id:         row.id,
    storage_mb: Number(row.storage_mb  ?? 0),
    price_usd:  parseFloat(Number(row.price_usd ?? 0).toFixed(2)),
    highlight:  Boolean(Number(row.highlight  ?? 0)),
    is_active:  Boolean(Number(row.is_active  ?? 1)),
    sort_order: Number(row.sort_order   ?? 0),
    features,
  };
}

async function timedWriteFetch(path, options = {}, ms = TIMEOUT_MS) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), ms);
  try {
    const res = await writeFetch(path, { ...options, signal: controller.signal });
    return res;
  } catch (err) {
    if (err.name === 'AbortError') throw new Error(`Request timed out after ${ms / 1000}s.`);
    throw err;
  } finally {
    clearTimeout(timer);
  }
}

async function parseNCBResponse(res, context = 'request') {
  let data;
  try { data = await res.json(); } catch {
    throw new Error(`Server returned an unreadable response (HTTP ${res.status}).`);
  }
  if (!res.ok) {
    const msg = data?.error || data?.message || `HTTP ${res.status}`;
    throw new Error(`${context} failed: ${msg}`);
  }
  return data;
}

export async function fetchActivePlans() {
  try {
    const res  = await timedWriteFetch(`read/${PLANS_TABLE}?is_active=1&sort=sort_order&order=asc&limit=50`);
    const data = await res.json().catch(() => ({}));
    const rows = Array.isArray(data.data) ? data.data : [];
    if (rows.length === 0) return DEFAULT_PLANS.filter(p => p.is_active);
    return rows.map(parsePlan).filter(Boolean);
  } catch (err) {
    console.warn('[PlansService] fetchActivePlans error:', err.message);
    return DEFAULT_PLANS.filter(p => p.is_active);
  }
}

export async function fetchPlanByKey(planKey) {
  try {
    const res  = await timedWriteFetch(`read/${PLANS_TABLE}?plan_key=${encodeURIComponent(planKey)}&limit=1`);
    const data = await res.json().catch(() => ({}));
    const row  = Array.isArray(data.data) && data.data.length > 0 ? data.data[0] : null;
    if (row) return parsePlan(row);
    return DEFAULT_PLANS.find(p => p.plan_key === planKey) ?? null;
  } catch (err) {
    return DEFAULT_PLANS.find(p => p.plan_key === planKey) ?? null;
  }
}

export async function adminFetchAllPlans(_adminToken) {
  let res;
  try {
    res = await timedWriteFetch(`read/${PLANS_TABLE}?sort=sort_order&order=asc&limit=100`);
  } catch (err) {
    return DEFAULT_PLANS.map(parsePlan);
  }
  let data;
  try { data = await res.json(); } catch {
    throw new Error(`Server returned an unreadable response (HTTP ${res.status}).`);
  }
  if (!res.ok) throw new Error(data?.error || `Failed to fetch plans (HTTP ${res.status}).`);
  
  const rows = Array.isArray(data.data) ? data.data : [];
  if (rows.length === 0) return DEFAULT_PLANS.map(parsePlan);
  return rows.map(parsePlan).filter(Boolean);
}

export async function adminCreatePlan(planData, _adminToken) {
  let featuresArr = planData.features;
  if (typeof featuresArr === 'string') featuresArr = featuresArr.split('\n').map(f => f.trim()).filter(Boolean);
  if (!Array.isArray(featuresArr)) featuresArr = [];

  const payload = {
    plan_key:      String(planData.plan_key || '').trim().toLowerCase(),
    name:          String(planData.name || '').trim(),
    description:   String(planData.description || '').trim(),
    storage_mb:    Number(planData.storage_mb),
    price_usd:     parseFloat(Number(planData.price_usd).toFixed(2)),
    billing_cycle: String(planData.billing_cycle || 'month'),
    features:      JSON.stringify(featuresArr),
    highlight:     planData.highlight ? 1 : 0,
    is_active:     planData.is_active !== false ? 1 : 0,
    sort_order:    Number(planData.sort_order ?? 0),
    color:         String(planData.color || '').trim(),
    badge:         String(planData.badge || '').trim(),
    owner_id:      'system',
  };

  const res  = await timedWriteFetch(`create/${PLANS_TABLE}`, { method: 'POST', body: JSON.stringify(payload) });
  return parseNCBResponse(res, 'Create plan');
}

export async function adminUpdatePlan(planId, updates, _adminToken) {
  const payload = {};
  if (updates.name          !== undefined) payload.name          = String(updates.name).trim();
  if (updates.description   !== undefined) payload.description   = String(updates.description).trim();
  if (updates.storage_mb    !== undefined) payload.storage_mb    = Number(updates.storage_mb);
  if (updates.price_usd     !== undefined) payload.price_usd     = parseFloat(Number(updates.price_usd).toFixed(2));
  if (updates.billing_cycle !== undefined) payload.billing_cycle = String(updates.billing_cycle);
  if (updates.features !== undefined) {
    let featArr = updates.features;
    if (typeof featArr === 'string') featArr = featArr.split('\n').map(f => f.trim()).filter(Boolean);
    if (!Array.isArray(featArr)) featArr = [];
    payload.features = JSON.stringify(featArr);
  }
  if (updates.highlight  !== undefined) payload.highlight  = updates.highlight  ? 1 : 0;
  if (updates.is_active  !== undefined) payload.is_active  = updates.is_active  ? 1 : 0;
  if (updates.sort_order !== undefined) payload.sort_order = Number(updates.sort_order);
  if (updates.color      !== undefined) payload.color      = String(updates.color).trim();
  if (updates.badge      !== undefined) payload.badge      = String(updates.badge).trim();

  const res  = await timedWriteFetch(`update/${PLANS_TABLE}/${planId}`, { method: 'PUT', body: JSON.stringify(payload) });
  return parseNCBResponse(res, 'Update plan');
}

export async function adminTogglePlan(planId, _adminToken) {
  const getRes  = await timedWriteFetch(`read/${PLANS_TABLE}/${planId}`);
  const getData = await parseNCBResponse(getRes, 'Fetch plan for toggle');
  const current = getData.data ?? getData;
  const newActive = Boolean(Number(current.is_active)) ? 0 : 1;

  const putRes  = await timedWriteFetch(`update/${PLANS_TABLE}/${planId}`, { method: 'PUT', body: JSON.stringify({ is_active: newActive }) });
  const putData = await parseNCBResponse(putRes, 'Toggle plan');
  return { ...putData, is_active: Boolean(newActive) };
}

export async function adminDeletePlan(planId, _adminToken) {
  const getRes  = await timedWriteFetch(`read/${PLANS_TABLE}/${planId}`);
  const getData = await parseNCBResponse(getRes, 'Fetch plan for delete');
  const existing = getData.data ?? getData;

  const isSystem = ['free', 'starter', 'family', 'legacy', 'will_draft', 'will_guardian', 'will_estate'].includes(existing?.plan_key)
    && existing?.owner_id === 'system';

  if (isSystem) {
    const putRes = await timedWriteFetch(`update/${PLANS_TABLE}/${planId}`, { method: 'PUT', body: JSON.stringify({ is_active: 0 }) });
    await parseNCBResponse(putRes, 'Deactivate system plan');
    return { soft: true, message: 'System plan deactivated.' };
  }

  const res  = await timedWriteFetch(`delete/${PLANS_TABLE}/${planId}`, { method: 'DELETE' });
  return parseNCBResponse(res, 'Delete plan');
}

export async function adminTriggerSeed(_adminToken) {
  let existingKeys = new Set();
  try {
    const res  = await timedWriteFetch(`read/${PLANS_TABLE}?limit=100`);
    const data = await res.json().catch(() => ({}));
    const rows = Array.isArray(data.data) ? data.data : [];
    existingKeys = new Set(rows.map(r => r.plan_key).filter(Boolean));
  } catch (err) {
    return { seeded: [], skipped: [], error: `Could not read existing plans: ${err.message}` };
  }

  const toInsert = DEFAULT_PLANS.filter(p => !existingKeys.has(p.plan_key));
  const seeded   = [];
  const failures = [];

  for (const plan of toInsert) {
    try {
      await adminCreatePlan(plan, null);
      seeded.push(plan.plan_key);
    } catch {
      failures.push(plan.plan_key);
    }
  }

  const skipped = DEFAULT_PLANS.map(p => p.plan_key).filter(k => existingKeys.has(k));
  return {
    seeded,
    skipped,
    error:   failures.length > 0 ? `Failed: ${failures.join(', ')}` : null,
    message: `${seeded.length} seeded, ${skipped.length} already existed.`,
  };
}

export async function ensureDefaultPlansSeeded() {
  try {
    // Only run this once per session to avoid repeated calls
    if (sessionStorage.getItem('plans_seeded') === 'true') return;
    
    const result = await adminTriggerSeed(null);
    if (result.error) {
      console.warn('[PlansService] ensureDefaultPlansSeeded issue:', result.error);
    } else {
      console.log(`[PlansService] ensureDefaultPlansSeeded success: ${result.message}`);
      sessionStorage.setItem('plans_seeded', 'true');
    }
  } catch (err) {
    console.error('[PlansService] ensureDefaultPlansSeeded error:', err);
  }
}